import { jwtDecode } from "jwt-decode";
import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { getAccessToken, getSelectedInstanceId } from "@/generic/authStore";

type Token = {
    exp: number;
    iat: number;
    instanceId: number;
    instances: number[];
    scope: string[];
    sub: string;
    username: string;
};

export default Token;

export const getTimeLeft = (token: Token) => {
    return token.exp * 1000 - Date.now();
};

type KeycloakClaims = {
    exp: number;
    iat: number;
    sub: string;
    preferred_username?: string;
    realm_access?: { roles?: string[] };
};

/** Reads the current Keycloak access token (for UI role gating) plus the locally
 *  selected instance. Roles surface as SCOPE_* to match existing checks. */
export const getTokenData = (): Token | null => {
    const accessToken = getAccessToken();
    if (!accessToken) {
        return null;
    }
    try {
        const claims = jwtDecode<KeycloakClaims>(accessToken);
        const roles = claims.realm_access?.roles ?? [];
        // Expand the role hierarchy (SUPERUSER ⇒ ADMIN ⇒ USER ⇒ VIEWER) so UI
        // checks like scope.includes("ADMIN") work for higher roles too. Raw role
        // names (not SCOPE_-prefixed) to match the existing UI gating.
        const hierarchy = ["SUPERUSER", "ADMIN", "USER", "VIEWER"];
        const scope = new Set<string>();
        roles.forEach((role) => {
            const idx = hierarchy.indexOf(role);
            if (idx >= 0) {
                hierarchy.slice(idx).forEach((r) => scope.add(r));
            } else {
                scope.add(role);
            }
        });
        return {
            exp: claims.exp,
            iat: claims.iat,
            instanceId: getSelectedInstanceId() ?? -1,
            instances: [],
            scope: Array.from(scope),
            sub: claims.sub,
            username: claims.preferred_username ?? claims.sub,
        };
    } catch {
        return null;
    }
};

const HIERARCHY = ["SUPERUSER", "ADMIN", "USER", "VIEWER"];

function expandScopes(roles: string[]): string[] {
    const scope = new Set<string>();
    roles.forEach((role) => {
        const idx = HIERARCHY.indexOf(role);
        if (idx >= 0) {
            HIERARCHY.slice(idx).forEach((r) => scope.add(r));
        } else {
            scope.add(role);
        }
    });
    return Array.from(scope);
}

/** Reactive roles/scopes from the live OIDC token — use for UI role gating so the
 *  menu re-renders once the token finishes loading (e.g. after a page reload),
 *  instead of reading the non-reactive module store once at mount. */
export function useScopes(): string[] {
    const auth = useAuth();
    return useMemo(() => {
        const accessToken = auth.user?.access_token;
        if (!accessToken) return [];
        try {
            return expandScopes(jwtDecode<KeycloakClaims>(accessToken).realm_access?.roles ?? []);
        } catch {
            return [];
        }
    }, [auth.user]);
}
